/**
 * Landing page for /sso/error?reason=...
 *
 * The backend deliberately sends a short reason code rather than a technical
 * message: the detail stays in server logs where it is useful, and the user
 * gets something they can act on. Anything unrecognised falls back to generic
 * copy rather than rendering a raw code at someone.
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../Auth.scss';

const REASONS = {
  expired: {
    title: 'Your sign-in request timed out',
    body: 'Sign-in requests are only valid for a few minutes. Please start again.',
  },
  invalid_assertion: {
    title: 'We couldn’t verify your sign-in',
    body:
      'Your identity provider’s response could not be validated. This is usually a '
      + 'configuration problem rather than anything you did — please contact your IT team.',
  },
  not_authenticated: {
    title: 'Your organisation didn’t confirm your sign-in',
    body: 'Please try again, or contact your IT team if this keeps happening.',
  },
  replayed: {
    title: 'This sign-in link has already been used',
    body: 'For your security each sign-in can only be used once. Please start again.',
  },
  not_federated: {
    title: 'Single sign-on isn’t set up for your organisation',
    body: 'Please sign in with your email and password, or ask your IT team about enabling single sign-on.',
  },
  unknown_issuer: {
    title: 'We don’t recognise that identity provider',
    body:
      'The sign-in came from a directory we aren’t configured to trust. '
      + 'Please contact your IT team.',
  },
  mfa_required: {
    title: 'Multi-factor authentication is required',
    body:
      'Your organisation requires more than one authentication factor for this '
      + 'application. Please sign in again and complete the additional check.',
  },
  mfa_claim_missing: {
    title: 'Your provider didn’t report multi-factor authentication',
    body:
      'This connection requires proof that more than one factor was used, but '
      + 'your identity provider is not sending it. Signing in again will not help '
      + '— your IT team needs to adjust the connection.',
  },
  invalid_logout: {
    title: 'We couldn’t complete the sign-out',
    body:
      'Your session here has ended, but the sign-out message from your identity '
      + 'provider could not be validated. Please close your browser to be sure.',
  },
  not_provisioned: {
    title: 'You don’t have access to this workspace yet',
    body:
      'Your identity was confirmed, but no account is available for you here. '
      + 'Ask your administrator to grant you access.',
  },
};

const FALLBACK = {
  title: 'Sign-in didn’t complete',
  body: 'Something went wrong while signing you in. Please try again.',
};

function SsoError() {
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason') || '';
  const { title, body } = REASONS[reason] || FALLBACK;

  return (
    <div className="sso_status_wrapper">
      <div className="sso_status_card">
        <h2 className="sso_status_title">{title}</h2>
        <p className="sso_status_text">{body}</p>
        <Link to="/" className="sso_status_btn">
          Back to sign in
        </Link>
        {reason && (
          // Support asks "what did the screen say?" -- this makes the answer
          // precise without putting jargon in the headline.
          <p className="sso_status_code">Reference: {reason}</p>
        )}
      </div>
    </div>
  );
}

export default SsoError;
